import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CustomerProfileMappingResponse,
  FillOutTemplateResponse,
  FillOutTemplateWithCaseDataRequest,
  FirstContactTemplateContext,
  GetFirstContactTemplateContextRequest,
  GetFirstContactTemplateNameRequest
} from '@secca/shared/models/atlas/atlas-template.model';
import { Observable } from 'rxjs';
import { AtlasApiService } from './atlas-api.service';

@Injectable({
  providedIn: 'root'
})
export class AtlasTemplateService {
  private readonly firstContactTemplatePath = '/api/v1/first-contact-template/case';

  constructor(
    private http: HttpClient,
    private atlasApiService: AtlasApiService
  ) {}

  fillOutTemplateUsingCaseData(caseNumber: string, request: FillOutTemplateWithCaseDataRequest): Observable<FillOutTemplateResponse> {
    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.post<FillOutTemplateResponse>(
        this.atlasApiService.buildUrl(`${this.firstContactTemplatePath}/${this.encodePathSegment(caseNumber)}/fill-out`),
        request,
        { headers }
      )
    );
  }

  getLatestFillOutTemplateForCase(caseNumber: string): Observable<FillOutTemplateResponse> {
    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.get<FillOutTemplateResponse>(
        this.atlasApiService.buildUrl(`${this.firstContactTemplatePath}/${this.encodePathSegment(caseNumber)}/fill-out`),
        { headers }
      )
    );
  }

  getFirstContactTemplate(teamCountry: string, templateName: string): Observable<string[]> {
    const params = new HttpParams()
      .set('team_country', teamCountry)
      .set('template_name', templateName);

    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.get<string[]>(this.atlasApiService.buildUrl('/get_first_contact_template'), { headers, params })
    );
  }

  getCustomerProfileMapping(customerProfileId: number): Observable<CustomerProfileMappingResponse> {
    const params = new HttpParams().set('customer_profile_id', String(customerProfileId));
    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.get<CustomerProfileMappingResponse>(this.atlasApiService.buildUrl('/get_customer_profile_mapping'), { headers, params })
    );
  }

  getFirstContactTemplateName(request: GetFirstContactTemplateNameRequest): Observable<string> {
    const params = new HttpParams()
      .set('customer_profile_market', request.customer_profile_market)
      .set('cause_level_1_code', request.cause_level_1_code)
      .set('cause_level_2_code', request.cause_level_2_code)
      .set('cause_level_3_code', request.cause_level_3_code);

    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.get(this.atlasApiService.buildUrl('/get_first_contact_template_name'), { headers, params, responseType: 'text' })
    );
  }

  getFirstContactTemplateContext(request: GetFirstContactTemplateContextRequest): Observable<FirstContactTemplateContext> {
    let params = new HttpParams();

    if (request.customer_profile_id != null) {
      params = params.set('customer_profile_id', String(request.customer_profile_id));
    }

    if (request.cause_level_1_code != null) {
      params = params.set('cause_level_1_code', request.cause_level_1_code);
    }

    if (request.cause_level_2_code != null) {
      params = params.set('cause_level_2_code', request.cause_level_2_code);
    }

    if (request.cause_level_3_code != null) {
      params = params.set('cause_level_3_code', request.cause_level_3_code);
    }

    if (request.winter_sport_enabled != null) {
      params = params.set('winter_sport_enabled', String(request.winter_sport_enabled));
    }

    if (request.winther_sport_enabled != null) {
      params = params.set('winther_sport_enabled', String(request.winther_sport_enabled));
    }

    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.get<FirstContactTemplateContext>(
        this.atlasApiService.buildUrl(`${this.firstContactTemplatePath}/${this.encodePathSegment(request.case_number)}`),
        { headers, params }
      )
    );
  }

  private encodePathSegment(value: string): string {
    return encodeURIComponent(value);
  }
}
